import React, { useEffect, useState } from 'react';
import { Button, EButtonType, GoogleApiWrapper, InfoWindow, KitIcons, Map, Marker, Polygon } from '@repo/apg-ui';
import './MapContainer.scss';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import parse from 'html-react-parser';
import { memoizedGetPaths } from '../../utils/map';
import { Product, productsActions } from '../../store/products';
import { AppState } from '../../store';
import { bindActionCreators, Dispatch } from 'redux';
import * as _actionsBasket from '../../store/products/actions';
import { connect } from 'react-redux';
import { addWeeks, endOfWeek, parseISO, startOfWeek } from 'date-fns';

const POLYGON_COLOR = '#B30200';

export enum MarkerColors {
  Red = '#B30200',
  Green = '#00C32C',
  Grey = '#817F7F',
  Yellow = '#d2a036',
}

const getZoomLevel = (isMobile) => (isMobile ? 11 : 14);

interface Props {
  center: any;
  google: any;
  locations: any;
  zoom?: number;
  isProductDetailModal?: boolean;
  basket?: any;
  addToBasket?: (product: Product, producturn: string, callback?: any) => void;
  removeFromBasket?: (basketEntryId: string, producturn: string, callback?: any) => void;
}

export interface Position {
  lat: number;
  lng: number;
}

type Location = {
  latitude: number;
  longitude: number;
  address: string;
};

export const MapContainer = ({
  center,
  google,
  locations,
  zoom: initialZoom,
  isProductDetailModal,
  basket,
  addToBasket,
  removeFromBasket,
}: Props) => {
  const [activeMarker, setActiveMarker] = useState(null);
  const [activeMarkerSubPoint, setActiveMarkerSubPoint] = useState(null);
  const [showingInfoWindow, setShowingInfoWindow] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(initialZoom || getZoomLevel(window.innerWidth < 767)); // Default zoom level based on initial client width
  const intl = useIntl();
  const [markersLoadingState, setMarkersLoadingState] = useState({});

  useEffect(() => {
    console.log('rerender');
    const handleResize = () => {
      const isMobile = window.innerWidth < 767;
      setCurrentZoom(getZoomLevel(isMobile));
    };

    window.addEventListener('resize', handleResize);

    const year = 2025;
    const start = startOfWeek(parseISO(new Date(`${year}-01-01`).toISOString()), {
      weekStartsOn: 1,
    });

    let weekStartDate = addWeeks(start, 15 - 1);

    if (start.getTimezoneOffset() > weekStartDate.getTimezoneOffset()) {
      weekStartDate = addWeeks(start, 15);
    }

    // Calculate the end date as the Sunday of the selected week
    const weekEndDate = endOfWeek(weekStartDate, {
      weekStartsOn: 1,
    });

    console.log(start.getTimezoneOffset());
    console.log(weekEndDate.getTimezoneOffset());

    console.log(year, start);
    console.log('addWeeks(start, 15 - 1).toISOString()', addWeeks(start, 15).toISOString());
    console.log(weekStartDate, weekEndDate);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onMarkerClick = (props, marker, subPointIndex = undefined) => {
    setActiveMarker(marker);
    const markerLoaders = {
      ...markersLoadingState,
      [marker.name]: false,
    };
    setMarkersLoadingState(markerLoaders);
    setShowingInfoWindow(true);
    if (subPointIndex != undefined && subPointIndex > -1) {
      setActiveMarkerSubPoint(subPointIndex);
    }
  };

  const onMapClicked = () => {
    if (showingInfoWindow) {
      setActiveMarker(null);
      setShowingInfoWindow(false);
      setActiveMarkerSubPoint(null);
    }
  };

  const handleClicks = (e) => {
    if (e.srcElement.id.includes('productSelect')) {
      handleBasketClick();
    }
  };

  const handleBasketClick = () => {
    const keyAddress = activeMarker?.name;
    const product = locations[keyAddress];

    const isInBasket = basket.basketProducts.productsByUrn[product?.producturn];

    setMarkersLoadingState({
      ...markersLoadingState,
      [product?.producturn]: true,
    });
    if (isInBasket) {
      removeFromBasket(
        basket.basketProducts?.productsByUrn[product?.producturn].basketentryid,
        product?.producturn,
        () => {
          setMarkersLoadingState({
            ...markersLoadingState,
            [product?.producturn]: false,
          });
        },
      );
    } else {
      addToBasket(product, product.producturn, () => {
        setMarkersLoadingState({
          ...markersLoadingState,
          [product?.producturn]: false,
        });
      });
    }
  };

  const infoWindowOpened = (infoWindow) => {
    (document as any).querySelector('#infoWindowWrapper') &&
      (document as any).querySelector('#infoWindowWrapper').removeEventListener('click', handleClicks);
    (document as any).querySelector('#infoWindowWrapper') &&
      (document as any).querySelector('#infoWindowWrapper').addEventListener('click', handleClicks);
  };

  const renderInfoWindowContent = () => {
    const keyAddress = activeMarker?.name;
    const productData = locations[keyAddress];
    const isMarkerColorGrey = false;

    const popupInfoData = {
      image: productData?.productphotopath,
      name: productData?.name,
      format: productData?.format,
      attributeshortcuts: productData?.attributeshortcuts,
      productid: productData?.productid,
      state: productData?.state,
    };

    const isInBasket = basket.basketProducts.productsByUrn[productData?.producturn];

    if (activeMarkerSubPoint > -1) {
      const subPointData = productData.subpoints[activeMarkerSubPoint];
      popupInfoData.productid = subPointData?.number || popupInfoData?.productid;
      popupInfoData.name = subPointData?.name || popupInfoData?.name;
      popupInfoData.image = subPointData?.image || popupInfoData?.image;
      popupInfoData.format = subPointData?.format || popupInfoData?.format;
    }

    const addressParsed = parse(String(popupInfoData.name));
    const isLoading = markersLoadingState[productData?.producturn] || false;

    return (
      <div id='infoWindowWrapper' className='info-popup'>
        <div key={`windowContent-${keyAddress}`} id='infoWindowWrapper' className='info-popup'>
          <div
            className={classNames('col p-0 w-100', {
              'product-image': !popupInfoData.image,
            })}
          >
            <div className='product-img-wrapper'>
              <img alt='Product' className='product-img w-100' src={popupInfoData.image + '&resolution=thumbnail'} />
            </div>
          </div>

          <div className='p-16 d-flex flex-column justify-content-start product-info'>
            <h4 className='w-100 fw-bold font-20 mb-3'>{popupInfoData?.format}</h4>

            <div className='w-100 d-flex flex-row justify-content-start align-items-center'>
              <div className='badge bg-light product-code'>
                <span>ID: {popupInfoData?.productid}</span>
              </div>

              {isMarkerColorGrey && (
                <div className='d-flex justify-content-start align-items-center ms-4 availability'>
                  <span className='unavailable-checker ps-0'>
                    <KitIcons.IconClose />
                  </span>
                  <span className='ps-0 font-14 font-600'>
                    {intl.formatMessage({
                      id: 'ec.journey02.unavailable',
                      defaultMessage: 'Nicht verfügbar',
                    })}
                  </span>
                </div>
              )}

              {/*{popupInfoData.state >= 0 && (*/}
              {/*    <div className="product-state">*/}
              {/*        <div className="d-flex align-items-center">*/}
              {/*            {popupInfoData?.state === 0 && (*/}
              {/*                <div className="d-flex justify-content-start ms-4 availability">*/}
              {/*                    <span className="green-checker ps-0">*/}
              {/*                        <KitIcons.GreenCheckV2Icon/>*/}
              {/*                    </span>*/}
              {/*                    <span className="ps-0 font-14 font-600">*/}
              {/*                        {intl.formatMessage({*/}
              {/*                            id: "ec.journey02.available",*/}
              {/*                            defaultMessage: "Verfügbar",*/}
              {/*                        })}*/}
              {/*                    </span>*/}
              {/*                </div>*/}
              {/*            )}*/}
              {/*            {popupInfoData?.state > 0 && (*/}
              {/*                <div*/}
              {/*                    className="d-flex justify-content-center align-items-center ms-4 availability">*/}
              {/*                    <span className="unavailable-checker ps-0">*/}
              {/*                        <KitIcons.CloseRedIcon/>*/}
              {/*                    </span>*/}

              {/*                    <span className="ps-0 font-14 font-600">*/}
              {/*                        {intl.formatMessage({*/}
              {/*                            id: "ec.journey02.unavailable",*/}
              {/*                            defaultMessage: "Nicht verfügbar",*/}
              {/*                        })}*/}
              {/*                    </span>*/}
              {/*                </div>*/}
              {/*            )}*/}
              {/*        </div>*/}
              {/*    </div>*/}
              {/*)}*/}
            </div>

            <span className='font-400 mt-3 address'>
              <span className='font-600 font-16'>
                <KitIcons.AddressIcon />
                {intl.formatMessage({
                  id: 'ec.journey02.address',
                  defaultMessage: 'Adresse',
                })}
              </span>
              <span className='font-14'>{addressParsed}</span>
            </span>

            <div className='d-flex flex-column justify-content-start ms-0 mt-3'>
              <Button
                className='font-600 buttonWithLoader'
                id={`productSelect${popupInfoData.productid}`}
                buttonType={isInBasket ? EButtonType.InvertedPillMedium : EButtonType.PillMedium}
                isLoading={isLoading}
                isDisabled={isLoading}
              >
                {!isLoading &&
                  (isInBasket
                    ? intl.formatMessage({
                        id: 'batchSelect.remove',
                        defaultMessage: 'Entfernen',
                      })
                    : intl.formatMessage({
                        id: 'batchSelect.add',
                        defaultMessage: 'Hinzufügen',
                      }))}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const addPaddingToBounds = (newBounds, latOffset = 0.0012, lngOffset = 0.0012) => {
    const center = newBounds.getCenter();
    const northEastPaddingPoint = new google.maps.LatLng(center.lat() + latOffset, center.lng() + lngOffset);
    const southWestPaddingPoint = new google.maps.LatLng(center.lat() - latOffset, center.lng() - lngOffset);
    newBounds.extend(northEastPaddingPoint);
    newBounds.extend(southWestPaddingPoint);
  };

  useEffect(() => {
    if (google && google.maps && locations) {
      const newBounds = new google.maps.LatLngBounds();

      Object.entries(locations).forEach(([key, location]) => {
        const subPoints = memoizedGetPaths(locations[key], false);

        if (!isEmpty(subPoints)) {
          subPoints.forEach((marker) => {
            if (marker) {
              newBounds.extend(new google.maps.LatLng(marker.lat, marker.lng));
            }
          });
        } else if (typeof location === 'object' && 'latitude' in location && 'longitude' in location) {
          const loc = location as Location;
          newBounds.extend(new google.maps.LatLng(loc.latitude, loc.longitude));
        }
      });
      if (isProductDetailModal) {
        addPaddingToBounds(newBounds);
      }
      setBounds(newBounds);
    }
  }, [locations]);

  return (
    <div className='map-container'>
      {typeof window !== 'undefined' &&
        google && ( // Ensure google is available and rendering occurs client-side
          <Map
            google={google}
            onClick={onMapClicked}
            zoom={currentZoom}
            // center={center}
            className='map'
            initialCenter={{ lat: 46.8011, lng: 8.2267 }}
            style={{ width: '100%', height: '100%' }}
            bounds={bounds}
          >
            {Object.entries(locations).map(([key, location]) => {
              const producturn = locations[key].producturn;
              const address = locations[key].address;

              const productData = locations[key];
              const paths = memoizedGetPaths(productData) as Position[][];
              const subPoints = productData.subpoints;
              const isExistSubPoints = !isEmpty(subPoints);
              const isSelected = key === activeMarker?.name;
              const isInBasket = basket.basketProducts.productsByUrn[producturn];

              if (isExistSubPoints && isProductDetailModal) {
                return [
                  productData?.area?.length > 2 ? (
                    <Polygon
                      paths={paths}
                      strokeColor={POLYGON_COLOR}
                      strokeWeight={2}
                      fillColor={POLYGON_COLOR}
                      fillOpacity={0.2}
                      key={`polygon-${productData?.number}`}
                    />
                  ) : null,
                  subPoints.map((marker, idx) => {
                    if (marker) {
                      return (
                        <Marker
                          onClick={(props, marker) => {
                            onMarkerClick(props, marker, idx);
                          }}
                          key={`marker-${key}-${marker.lat}-${marker.lng}-${idx}`}
                          idx={idx}
                          title={address}
                          name={producturn || address}
                          useCustomMarker={true}
                          position={{
                            lat: marker.y,
                            lng: marker.x,
                          }}
                          circle={null}
                          onMouseover={null}
                          onMouseout={null}
                          colorCustomMarker={isSelected ? MarkerColors.Red : MarkerColors.Green}
                        />
                      );
                    }

                    return null;
                  }),
                ];
              } else if (
                typeof location === 'object' &&
                'latitude' in location &&
                'longitude' in location &&
                'address' in location
              ) {
                const loc = location as Location;
                return (
                  <Marker
                    key={`${key}-${isSelected}`}
                    onClick={onMarkerClick}
                    position={{
                      lat: loc.latitude,
                      lng: loc.longitude,
                    }}
                    title={loc.address}
                    name={key}
                    useCustomMarker={true}
                    colorCustomMarker={isSelected || isInBasket ? MarkerColors.Red : MarkerColors.Green}
                  />
                );
              } else {
                return null;
              }
            })}

            {activeMarker && showingInfoWindow && (
              <InfoWindow
                marker={activeMarker}
                onClose={() => {
                  setShowingInfoWindow(false);
                  setActiveMarker(null);
                }}
                onOpen={infoWindowOpened}
                visible={showingInfoWindow}
              >
                {renderInfoWindowContent()}
              </InfoWindow>
            )}
          </Map>
        )}
    </div>
  );
};

function mapStateToProps(state: AppState, ownProps: any) {
  return {
    ...ownProps,
    ...state.products,
    ...state.locations,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(productsActions as any, dispatch),
    actionsBasket: bindActionCreators(_actionsBasket as any, dispatch),
    dispatch: dispatch,
  };
}

export default GoogleApiWrapper({
  apiKey: 'AIzaSyBWgAtnXgNu_7we80pt4jSSCO-XepRj3u4',
})(connect(mapStateToProps, mapDispatchToProps)(MapContainer));
